import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { LanguageContext } from "../context/LanguageContext";
import styled from "styled-components";

// Styling matching CodiCal guidelines (Glassmorphism + clean spacing)
const BlogContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 20px;
  min-height: 80vh;
`;

const BlogTitle = styled.h1`
  text-align: center;
  font-size: 2.8rem;
  color: var(--primary);
  margin-bottom: 20px;
`;

const BlogDesc = styled.p`
  text-align: center;
  font-size: 1.1rem;
  color: var(--text-color);
  opacity: 0.8;
  margin-bottom: 60px;
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 30px;
`;

const PostCard = styled(Link)`
  text-decoration: none;
  border-radius: 24px;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;
  color: var(--card-text);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0,0,0,0.1);
  }
`;

const PostImage = styled.img`
  width: 100%;
  height: 220px;
  object-fit: cover;
  border-bottom: 1px solid var(--border-color);
`;

const PostContent = styled.div`
  padding: 24px;
`;

const PostDate = styled.span`
  display: block;
  font-size: 0.85rem;
  color: var(--primary);
  margin-bottom: 10px;
  font-weight: 600;
`;

const PostTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 10px;
  line-height: 1.4;
`;

const PostExcerpt = styled.p`
  font-size: 1rem;
  opacity: 0.8;
  line-height: 1.6;
`;

export default function Blog() {
  const { language } = useContext(LanguageContext);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const url = window.location.hostname === 'localhost' ? 'http://localhost/api/blog.php' : '/api/blog.php';
        const res = await axios.get(url);
        if (res.data.status === 'success') setPosts(res.data.posts);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  return (
    <BlogContainer dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <Helmet>
        <title>{language === "ar" ? "المدونة | CodiCal" : "Blog | CodiCal"}</title>
        <meta name="description" content={language === "ar" ? "اقرأ أحدث المقالات التقنية والبرمجية من كوديكال" : "Read the latest tech articles from CodiCal"} />
      </Helmet>

      <BlogTitle>{language === 'ar' ? 'مدونة كوديكال' : 'CodiCal Blog'}</BlogTitle>
      <BlogDesc>{language === 'ar' ? 'محتوى تقني، أفكار حديثة وحلول برمجية تلهمك بقلم خبرائنا.' : 'Tech content, modern ideas, and software solutions to inspire you.'}</BlogDesc>
      
      {loading ? (
        <div style={{textAlign:'center', marginTop:'50px'}}><div className="spin" style={{display:'inline-block', fontSize:'30px'}}>⚙</div></div>
      ) : (
        <PostsGrid>
          {posts.map(post => (
            <PostCard to={`/blog/${post.id}`} key={post.id}>
              {post.image_url ? (
                <PostImage src={post.image_url} alt={post.title_ar} />
              ) : (
                <div style={{height: '220px', background: 'linear-gradient(135deg, #0A2E4D, #1E6F8E)'}} />
              )}
              <PostContent>
                <PostDate>{new Date(post.created_at).toLocaleDateString()}</PostDate>
                <PostTitle>{language === 'ar' ? post.title_ar : post.title_en}</PostTitle>
                <PostExcerpt>{post.seo_description?.substring(0, 100)}...</PostExcerpt>
              </PostContent>
            </PostCard>
          ))}
          {posts.length === 0 && <p style={{textAlign: 'center', gridColumn: '1 / -1'}}>{language === 'ar' ? 'لا توجد مقالات حالياً' : 'No articles available.'}</p>}
        </PostsGrid>
      )}
    </BlogContainer>
  );
}
